import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ClipboardList,
  Clock3,
  FilePenLine,
  LayoutDashboard,
  ListChecks,
  Package,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  Warehouse,
  WifiOff,
  CalendarDays,
  Sun,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../../components/new-components/ui/button";
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from "../../../components/workspaceSidebar";
import { ViewTransition } from "../../../components/shadcn/motion";
import { patientName, shortDate, manifestLinkOf, closureSentence } from "../providerApi";
import type { RosterLens } from "../providerApi";
import { FieldView, PracticeId, practices } from "./practices";
import type { FieldWorkspaceProps } from "./contracts";
import { Notice, Section } from "./FieldPrimitives";
import PracticeSwitcher, { practiceStatusOf, usePractices } from "./clinical/PracticeSwitcher";
import PodiatryWorkspace from "./PodiatryWorkspace";
import FacilityPicker from "./FacilityPicker";
import Arrival from "./FieldArrival";
import Manifest from "./FieldManifest";
import DeviceReadiness from "./FieldDevices";
import MyStock from "./MyStock";
import "./field-workspace.css";

const NAV = [
  { key: "day", label: "My day", Icon: Sun },
  { key: "today", label: "Overview", Icon: LayoutDashboard },
  { key: "manifest", label: "Facility rounds", Icon: ClipboardList },
  { key: "atd-all", label: "ATD · all facilities", Icon: PackageCheck },
  { key: "twl", label: "TWL pipeline", Icon: ListChecks },
  { key: "schedule", label: "Visit plan", Icon: CalendarDays },
  { key: "followups", label: "Care follow-ups", Icon: FilePenLine },
  { key: "devices", label: "Device readiness", Icon: Package },
  { key: "inventory", label: "My stock", Icon: Warehouse },
] as const;

/** `viewHref` is the shell's own path rule for a view (the sidebar's items are real links, so a view deep-links). */
export default function FieldWorkspace(props: FieldWorkspaceProps & { viewHref: (view: FieldView) => string }) {
  const {
    sheet,
    scope,
    facilityId,
    view,
    loading,
    loadedAt,
    onView,
    viewHref,
    onFacility,
    onRefresh,
    feedback,
    children,
  } = props;
  // The practice the provider CHOSE (in memory only: no route, no URL). null = no choice yet: the default below decides.
  const [chosen, setChosen] = useState<PracticeId | null>(null);
  const practicesLoad = usePractices(facilityId);
  const podiatryHere = practiceStatusOf(practicesLoad, "PODIATRY");
  // A Podiatry provider is one the server answers ACTIVE or NOT_AT_FACILITY for Podiatry: both are given only after the
  // practice authorization gate passed (routes/clinicalEncounters.js GET /practices). It is remembered across buildings
  // so a building without the service says so inside the Podiatry workspace instead of flipping the provider back.
  // Loading, an unanswered call, NOT_OPEN (said before the gate when the module is not installed) and NOT_AUTHORIZED
  // are never Podiatry: it is never enabled by default.
  const [podiatryProvider, setPodiatryProvider] = useState(false);
  useEffect(() => {
    if (podiatryHere === "ACTIVE" || podiatryHere === "NOT_AT_FACILITY") setPodiatryProvider(true);
    else if (podiatryHere === "NOT_AUTHORIZED") setPodiatryProvider(false);
  }, [podiatryHere]);
  const audiologyActive = practiceStatusOf(practicesLoad, "AUDIOLOGY") === "ACTIVE";
  // The answer decides in the same render it arrives in (the effect above only remembers it): waiting for the effect
  // would draw the Audiology workspace once, and start its reads, before Podiatry took over.
  const isPodiatryProvider =
    podiatryHere === "ACTIVE" || podiatryHere === "NOT_AT_FACILITY"
      ? true
      : podiatryHere === "NOT_AUTHORIZED"
        ? false
        : podiatryProvider;
  const practice: PracticeId = isPodiatryProvider
    ? chosen === "audiology" && audiologyActive
      ? "audiology"
      : "podiatry"
    : chosen === "podiatry" && podiatryHere === "ACTIVE"
      ? "podiatry"
      : "audiology";
  const setPractice = (next: PracticeId) => setChosen(next);
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  const current = scope.facilities.find((f) => f.id === facilityId);
  const title = {
    day: "Where you are going today.",
    today: "Your clinical day, in focus.",
    manifest: "Care at the point of service.",
    schedule: "A clear plan for every visit.",
    devices: "Ready for the next step.",
    inventory: "Your car is your mobile office.",
    followups: "Close the gaps in care.",
    "atd-all": "Every ATD, every facility.",
    twl: "Every TWL, followed through.",
  }[view];
  const subtitle = {
    day: "Your planned visits, where each building is, and what Scheduling wants done there.",
    today: "Your facility, your manifest, and the work that matters next.",
    manifest:
      "Move room by room. Keep clinical context close and record your work.",
    schedule: "Facility logistics, contacts, and the authoritative visit plan.",
    devices:
      "See residents awaiting fitting and resolve conflicting delivery evidence.",
    inventory: "What you carry: add, count and confirm your devices, and see what is loaded for each visit.",
    followups: "UMED paperwork that can be followed up at this facility.",
    "atd-all": "Approved and recently changed ATD residents across your buildings, nearest first when you ask.",
    twl: "The residents you qualified, where each one is with the Patient Advocates, and which have no follow-up yet.",
  }[view];
  const ml = sheet ? manifestLinkOf(sheet) : null;
  const stocks = scope.links?.stock_report;
  // Until the server answers which practices this building offers, neither workspace is drawn (the Audiology one would
  // flash, and fire its reads, for a Podiatry provider). An error or no answer is "unavailable" and falls through to the
  // existing Audiology workspace: Podiatry is never enabled by default.
  if (practicesLoad.state === "loading") {
    return (
      <div className="fw" data-testid="workspace-loading">
        <div className="fw-body">
          <main className="fw-main">
            <div className="fw-empty" role="status">
              <RefreshCw className="fw-spin" aria-hidden="true" />
              <h3>Opening your workspace…</h3>
              <p>Checking which practices are open for you at this building.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }
  if (practice === "podiatry") {
    // The other workspace's own views (My day, ATD, TWL, Visit plan) never show here; only the building's load state does.
    const stateBlock = view === "day" || view === "atd-all" || view === "twl" || view === "schedule" ? null : children;
    return (
      <PodiatryWorkspace
        sheet={sheet}
        scope={scope}
        facilityId={facilityId}
        loading={loading}
        loadedAt={loadedAt}
        offline={offline}
        practicesLoad={practicesLoad}
        onFacility={onFacility}
        onRefresh={onRefresh}
        onCheckIn={props.onCheckIn}
        feedback={feedback}
        stateBlock={stateBlock}
        switchTo={audiologyActive ? { label: practices.audiology.name, onSwitch: () => setChosen("audiology") } : null}
      />
    );
  }
  const sidebarGroups: SidebarGroup[] = [
    {
      key: "workspace",
      label: "Workspace",
      items: NAV.map(({ key, label, Icon }) => ({
        key,
        label,
        icon: Icon,
        href: viewHref(key),
        active: view === key,
      })),
    },
  ];
  return (
    <div className="fw">
      <a className="fw-skip" href="#fw-main">
        Skip to clinical workspace
      </a>
      <WorkspaceLayout workspace="PROVIDER" navLabel="Clinical workspace" groups={sidebarGroups}>
      <div className="fw-body">
        <header className="fw-topbar">
          <div>
            <span>eMerge / </span>
            <strong>Provider workspace</strong>
            <span> / {NAV.find((n) => n.key === view)?.label}</span>
          </div>
          <div className="fw-topbar-right">
            <span className={`fw-live${offline ? " offline" : ""}`}>
              <span />
              {offline ? "Offline" : "Connected workspace"}
            </span>
            {feedback}
          </div>
        </header>
        <main id="fw-main" className="fw-main" tabIndex={-1}>
          <div className="fw-page-heading">
            <div>
              <div className="wsb-title">
                <SidebarTrigger />
                <div>
                  <p className="fw-eyebrow">
                    Provider field operations · {practices[practice].name}
                  </p>
                  <h1>{title}</h1>
                </div>
              </div>
              <p>{subtitle}</p>
            </div>
            <div className="fw-heading-side">
              <div className="fw-facility">
                <span>Facility</span>
                <FacilityPicker
                  facilities={scope.facilities}
                  value={facilityId}
                  fallbackName={sheet?.facility.name}
                  onChange={onFacility}
                />
                {scope.enterprise && (
                  <Link to="/provider">
                    Find another facility <ArrowUpRight />
                  </Link>
                )}
              </div>
              {/* The practice module moved here from the old dark sidebar: a select is not a navigation link. */}
              <div className="fw-practice">
                <span className="fw-practice-label">Practice module</span>
                <PracticeSwitcher facilityId={facilityId} value={practice} onChange={setPractice} />
              </div>
            </div>
          </div>
          <div className="fw-freshness">
            <span>
              {offline ? <WifiOff /> : <Clock3 />}
              {offline
                ? "Offline · showing the last loaded data. Saving is unavailable."
                : loading
                  ? "Refreshing facility data…"
                  : loadedAt
                    ? `Loaded ${loadedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · refresh to check for changes`
                    : "Waiting for facility data"}
            </span>
            <Button
              variant="ghost"
              onClick={onRefresh}
              disabled={loading || offline}
            >
              <RefreshCw className={loading ? "fw-spin" : ""} />
              Refresh
            </Button>
          </div>
          {current?.closure && (
            // A dropped building is still listed (the provider's access may still reach it) but is said to be
            // closed, so an empty manifest there is never mistaken for an empty building (verification 2026-10-19).
            <Notice>
              {closureSentence(current.closure)}.
              {/* Only a scheduling closure stops new plans; a building marked Dropped or merged may still have them. */}
              {current.closure.source === 'SCHEDULING_PROFILE' || !current.closure.source ? ' Scheduling plans no new visits here.' : ''}
              {current.closure.source === 'SCHEDULING_PROFILE' && current.closure.reason ? ` ${current.closure.reason}` : ''}
            </Notice>
          )}
          <ViewTransition viewKey={view}>
          {children}
          {sheet && !children && (
            <>
              {(view === "today" || view === "manifest") && (
                <Arrival
                  key={facilityId}
                  sheet={sheet}
                  onCheckIn={props.onCheckIn}
                  offline={offline}
                />
              )}
              {view === "today" && (
                <>
                  <div className="fw-metrics">
                    {[
                      {
                        label: "Current manifest",
                        value: sheet.roster.lenses?.manifest,
                        detail: "Residents for consideration",
                        Icon: ClipboardList,
                        target: "manifest" as FieldView,
                        lens: "manifest" as RosterLens,
                      },
                      {
                        label: "ATD · approved",
                        value: sheet.roster.lenses?.atd,
                        detail: "Approval is separate from dispense",
                        Icon: Package,
                        target: "manifest" as FieldView,
                        lens: "atd" as RosterLens,
                      },
                      {
                        label: "PA enrolled",
                        value: sheet.facility.enrolled_residents,
                        detail: "From the PA system of record",
                        Icon: ArrowUpRight,
                        target: "manifest" as FieldView,
                        lens: "enrolled" as RosterLens,
                      },
                      {
                        label: "Paperwork blocked",
                        value: sheet.counts.blockedUmed,
                        detail: "Facility UMED follow-up",
                        Icon: FilePenLine,
                        target: "followups" as FieldView,
                      },
                    ].map((m) => (
                      <button
                        className="fw-metric"
                        key={m.label}
                        onClick={() =>
                          m.lens ? props.onLens(m.lens) : onView(m.target)
                        }
                      >
                        <span>
                          {m.label}
                          <m.Icon />
                        </span>
                        <strong>{m.value ?? "—"}</strong>
                        <small>{m.detail}</small>
                      </button>
                    ))}
                  </div>
                  <div className="fw-columns">
                    <div>
                      <Manifest {...props} offline={offline} />
                    </div>
                    <div className="fw-rail">
                      <Section
                        title="Next on your round"
                        subtitle="The facility’s recorded work, in one place."
                      >
                        <div className="fw-panel-content fw-next">
                          <Button
                            variant="outline"
                            onClick={() => props.onLens("atd")}
                          >
                            <Package />
                            <span>
                              <strong>Review ATD residents</strong>
                              <small>
                                {sheet.roster.lenses?.atd ?? "—"} approved to
                                dispense
                              </small>
                            </span>
                            <ArrowRight />
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => onView("devices")}
                          >
                            <ClipboardList />
                            <span>
                              <strong>Check fitting readiness</strong>
                              <small>
                                {sheet.counts.pendingFitResidents ??
                                  sheet.fitResidents?.length ??
                                  "—"}{" "}
                                residents · {sheet.counts.pendingFits} device
                                records
                              </small>
                            </span>
                            <ArrowRight />
                          </Button>
                          {ml && (
                            <a
                              className="fw-resource"
                              href={ml.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open{" "}
                              {ml.reference
                                ? "Monday reference workbook"
                                : "facility workbook"}
                              <ArrowUpRight />
                            </a>
                          )}
                        </div>
                      </Section>
                      <Section
                        title="Clinical and PA ownership"
                        subtitle="Keep every handoff intentional."
                      >
                        <div className="fw-panel-content">
                          <div className="fw-enrolled">
                            <span>PA enrolled residents</span>
                            <strong>
                              {sheet.facility.enrolled_residents ??
                                "Not recorded"}
                            </strong>
                          </div>
                          <Notice>
                            Enrollment comes from the PA system of record.
                            Manifest notes remain clinical; saving them does not
                            create outreach, consent, or enrollment.
                          </Notice>
                        </div>
                      </Section>
                      <Section
                        title="Facility contacts"
                        subtitle="The people supporting your visit."
                      >
                        <div className="fw-panel-content">
                          {sheet.contacts.all.length ? (
                            sheet.contacts.all.map((c) => (
                              <div className="fw-contact" key={c.id}>
                                <strong>{c.name || "Name not recorded"}</strong>
                                <p>{c.title || "Role not recorded"}</p>
                                {c.phone && (
                                  <a href={`tel:${c.phone}`}>{c.phone}</a>
                                )}
                              </div>
                            ))
                          ) : (
                            <p className="fw-muted">
                              No facility contact is recorded.
                            </p>
                          )}
                        </div>
                      </Section>
                    </div>
                  </div>
                </>
              )}
              {view === "manifest" && <Manifest {...props} offline={offline} />}
              {view === "devices" && <DeviceReadiness sheet={sheet} offline={offline} />}
              {view === "followups" && (
                <Section
                  title="Paperwork follow-up"
                  subtitle="UMED requests blocked on information obtainable at this facility."
                >
                  {sheet.blockedUmed.length ? (
                    <ul className="fw-detail-list">
                      {sheet.blockedUmed.map((u) => (
                        <li key={u.id}>
                          <FilePenLine />
                          <div>
                            <strong>{patientName(u)}</strong>
                            <p>
                              Room {u.room_number || "not recorded"} ?{" "}
                              {u.status || "Status not recorded"}
                            </p>
                            <p>
                              {u.faxed_status || "Fax status not recorded"}
                              {u.submitted_date
                                ? ` ? submitted ${shortDate(u.submitted_date)}`
                                : ""}
                            </p>
                            <p>
                              Last follow-up:{" "}
                              {u.last_follow_up_date
                                ? shortDate(u.last_follow_up_date)
                                : "not recorded"}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="fw-empty">
                      <ShieldCheck />
                      <h3>No paperwork blocked here</h3>
                      <p>
                        The day sheet has no UMED requests waiting on
                        information from this facility.
                      </p>
                    </div>
                  )}
                </Section>
              )}

              {view === "inventory" && (
                // 2026-10-22: the provider's own stock (MyStock.tsx); the configured workbook stays as a secondary link.
                <MyStock stockReportUrl={stocks} offline={offline} />
              )}
            </>
          )}
          </ViewTransition>
          <div className="fw-scope">
            <ShieldCheck />
            <strong>Care, within your scope.</strong>
            <p>
              Facility and patient access come from your server-authorized
              assignments.
            </p>
          </div>
          <footer className="fw-footer">
            eMerge Clinical Operations <span>·</span> Facility scope and care
            assignment are enforced by the server. Changes in CounselEAR
            normally appear within about five minutes under healthy operating
            conditions.
          </footer>
        </main>
      </div>
      </WorkspaceLayout>
    </div>
  );
}
